import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { primaryRoleLabel } from '../utils/roles';

// TS port of the source (Falah) app's pages/SupplierDetail.jsx.
interface SupplierLinkedUser {
  id: number;
  full_name: string;
  email: string;
  roles: string[];
}

interface SupplierRecord {
  id: number;
  business_name: string;
  contact_person: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  bank_name: string | null;
  bank_account: string | null;
  notes: string | null;
  purchase_orders_count: number;
  vendor_invoices_count: number;
  users?: SupplierLinkedUser[];
}

export function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [supplier, setSupplier] = useState<SupplierRecord | null>(null);

  const canManage = can('procurement.suppliers.manage');

  useEffect(() => {
    api.get<SupplierRecord>(`/suppliers/${id}`).then((res) => setSupplier(res.data));
  }, [id]);

  const remove = async () => {
    if (!supplier) return;
    if (!(await confirmDelete(`Delete supplier "${supplier.business_name}"?`))) return;
    try {
      await api.delete(`/suppliers/${id}`);
      navigate('/procurement/suppliers');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!supplier) return null;

  const users = supplier.users ?? [];

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/procurement/suppliers')} />
          {canManage && <IconButton icon={IconEdit} label="Edit" to={`/procurement/suppliers/${id}/edit`} />}
          {canManage && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />}
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{supplier.business_name}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Contact person</span>
            <span className={`detail-item-value${supplier.contact_person ? '' : ' empty'}`}>
              {supplier.contact_person || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Phone</span>
            <span className={`detail-item-value${supplier.phone ? '' : ' empty'}`}>{supplier.phone || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Mobile</span>
            <span className={`detail-item-value${supplier.mobile ? '' : ' empty'}`}>{supplier.mobile || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Email</span>
            <span className={`detail-item-value${supplier.email ? '' : ' empty'}`}>{supplier.email || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Bank name</span>
            <span className={`detail-item-value${supplier.bank_name ? '' : ' empty'}`}>
              {supplier.bank_name || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Bank account</span>
            <span className={`detail-item-value${supplier.bank_account ? '' : ' empty'}`}>
              {supplier.bank_account || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Purchase orders</span>
            <span className="detail-item-value">{supplier.purchase_orders_count ?? 0}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Vendor invoices</span>
            <span className="detail-item-value">{supplier.vendor_invoices_count ?? 0}</span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {supplier.notes ? (
            <p className="detail-notes">{supplier.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Linked users</h2>
        <p className="muted">Portal accounts that sign in as this supplier.</p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name}</td>
                  <td className="muted">{u.email}</td>
                  <td className="muted">{primaryRoleLabel(u.roles)}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No users linked to this supplier.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
